import useOpenInterest from '@/features/markets/api/get-open-interest';
import useSymbol from '@/hooks/use-symbol';
import { useListenKey } from '@/lib/api/listen-key';
import { useAccountStore, useCurrentAccount } from '@/store/use-account-store';
import { useMarketStore } from '@/store/use-markets-store';
import { useOrdersStore } from '@/store/use-orders-store';
import { useWebSocketStore } from '@/store/use-websocket-store';
import { WsType } from '@/types';
import { useEffect, useMemo, useRef } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

const PUBLIC_WS_URL = `${import.meta.env.VITE_WS_V2_URL}?version=1.0&xwebsocketserver=restserver0`;
const PRIVATE_WS_URL = `${import.meta.env.VITE_WS_URL}?version=1.0&xwebsocketserver=restserver`;

const PUBLIC_CHANNELS = ['ticker'];

/**
 * WebSocket Handler Component
 *
 * This component manages the WebSocket connection related to the business logic of the app.
 *
 * Key features:
 * - Initializes public and private WebSocket connections, exported as `publicWs` and `privateWs`
 * - Manages auth and listen key (with refresh) for private
 * - Sets up default message handlers that put updates into the store
 * - Disconnects private connection on switch wallet/disconnect
 *
 * @component
 */

export default function WebSocketHandler() {
  const { hasPublicWs, privateWsByAddress, isPublicConnected } =
    useWebSocketStore(
      useShallow((state) => ({
        hasPublicWs: !!state.publicWs,
        isPublicConnected: state.isConnected[WsType.PUBLIC],
        privateWsByAddress: state.privateWsByAddress,
      })),
    );

  const {
    _hasHydrated,
    address,
    fundedModeActive,
    fundedUpdateAddresses,
    accounts,
    authParamsByAddress,
    getAuthParams,
  } = useAccountStore(
    useShallow((state) => ({
      _hasHydrated: state._hasHydrated,
      address: state.address,
      fundedModeActive: state.fundedModeActive,
      fundedUpdateAddresses: state.fundedUpdateAddresses,
      accounts: state.accounts,
      authParamsByAddress: state.authParamsByAddress,
      getAuthParams: state.getAuthParams,
    })),
  );

  const listenKeyPendingRef = useRef(new Set<string>());

  const { mutateAsync: getListenKey } = useListenKey();

  // Initialize Public WebSocket
  useEffect(() => {
    const { initializePublicWs } = useWebSocketStore.getState();

    initializePublicWs(PUBLIC_WS_URL);
  }, []);

  const privateAddresses = useMemo(() => {
    const addresses = new Set<string>();

    if (address) {
      addresses.add(address);
    }

    if (fundedModeActive) {
      fundedUpdateAddresses.forEach((fundedAddress) =>
        addresses.add(fundedAddress),
      );
    }

    return Array.from(addresses);
  }, [address, fundedModeActive, fundedUpdateAddresses]);

  useEffect(() => {
    if (!fundedModeActive) {
      return;
    }

    privateAddresses.forEach((targetAddress) => {
      if (listenKeyPendingRef.current.has(targetAddress)) {
        return;
      }

      const authParams = getAuthParams(targetAddress, { skipFallback: true });

      if (!authParams?.apiKey || authParams.listenKey) {
        return;
      }

      listenKeyPendingRef.current.add(targetAddress);

      getListenKey(targetAddress)
        .catch((error) => {
          console.warn(
            'Failed to get listen key for funded address',
            targetAddress,
            error,
          );
        })
        .finally(() => {
          listenKeyPendingRef.current.delete(targetAddress);
        });
    });
  }, [fundedModeActive, privateAddresses, authParamsByAddress]);

  // Initialize Private WebSocket(s)
  useEffect(() => {
    const { initializePrivateWs, disconnectWs } = useWebSocketStore.getState();

    if (!_hasHydrated) {
      disconnectWs(WsType.PRIVATE);
      return;
    }

    const targetAddresses = privateAddresses.filter((targetAddress) => {
      const authParams = getAuthParams(targetAddress, { skipFallback: true });

      return !!authParams?.listenKey;
    });

    const targetKeys = new Set(
      targetAddresses.map((targetAddress) => targetAddress.toLowerCase()),
    );

    Object.keys(privateWsByAddress).forEach((existingAddress) => {
      if (!targetKeys.has(existingAddress)) {
        disconnectWs(WsType.PRIVATE, false, { address: existingAddress });
      }
    });

    targetAddresses.forEach((targetAddress) => {
      const authParams = getAuthParams(targetAddress, { skipFallback: true });

      if (!authParams?.listenKey) {
        return;
      }

      const url = `${PRIVATE_WS_URL}${authParams.accountGroup}&listenKey=${authParams.listenKey}`;

      initializePrivateWs(targetAddress, url);
    });
  }, [_hasHydrated, privateAddresses, privateWsByAddress, authParamsByAddress]);

  const openSymbols = useCurrentAccount(
    useShallow((state) => Object.keys(state.positions)),
  );

  const fundedOpenSymbols = useMemo(() => {
    if (!fundedModeActive) {
      return [];
    }

    const symbols = new Set<string>();

    fundedUpdateAddresses.forEach((address) => {
      const positions = accounts[address]?.positions;

      if (!positions) {
        return;
      }

      Object.keys(positions).forEach((positionSymbol) =>
        symbols.add(positionSymbol),
      );
    });

    return Array.from(symbols);
  }, [fundedModeActive, fundedUpdateAddresses, accounts]);

  useEffect(() => {
    const { increaseDependenciesForSymbol, decreaseDependenciesForSymbol } =
      useWebSocketStore.getState();

    openSymbols.forEach((symbol) => increaseDependenciesForSymbol(symbol));

    return () => {
      openSymbols.forEach((symbol) => decreaseDependenciesForSymbol(symbol));
    };
  }, [openSymbols]);

  const symbol = useSymbol();

  const dependenciesBySymbol = useWebSocketStore(
    useShallow((state) => state.dependenciesBySymbol),
  );

  const symbols = useMemo(() => {
    const symbolsSet = new Set<string>();

    Object.keys(dependenciesBySymbol).forEach((key) => {
      if (dependenciesBySymbol[key] > 0) {
        symbolsSet.add(key);
      }
    });

    fundedOpenSymbols.forEach((fundedSymbol) => symbolsSet.add(fundedSymbol));

    if (symbol) {
      symbolsSet.add(symbol);
    }

    return Array.from(symbolsSet);
  }, [symbol, dependenciesBySymbol, fundedOpenSymbols]);

  const openOrderSymbols = useOrdersStore(
    useShallow((state) => {
      const { orders, newOrderIds } = state.getOrders(address);
      const set = new Set<string>();
      newOrderIds.forEach((id) => {
        const order = orders[id];
        if (order?.symbol) set.add(order.symbol);
      });
      return Array.from(set);
    }),
  );

  const depthSymbols = useMemo(() => {
    const symbolsSet = new Set<string>(symbols);
    openSymbols.forEach((positionSymbol) => symbolsSet.add(positionSymbol));
    fundedOpenSymbols.forEach((fundedSymbol) => symbolsSet.add(fundedSymbol));
    openOrderSymbols.forEach((orderSymbol) => symbolsSet.add(orderSymbol));
    return Array.from(symbolsSet);
  }, [symbols, openSymbols, fundedOpenSymbols, openOrderSymbols]);

  const symbolsKey = useMemo(() => symbols.slice().sort().join(','), [symbols]);

  const depthSymbolsKey = useMemo(
    () => depthSymbols.slice().sort().join(','),
    [depthSymbols],
  );

  useEffect(() => {
    if (!hasPublicWs || !isPublicConnected) return;
    const { publicWs } = useWebSocketStore.getState();
    if (!publicWs) return;
    const symbolsList = symbolsKey ? symbolsKey.split(',') : [];
    const channels = PUBLIC_CHANNELS.flatMap((channel) =>
      symbolsList.map((symbol) => `${symbol}@${channel}`),
    );
    if (channels.length === 0) return;
    publicWs.subscribeToChannels(channels);
    return () => {
      publicWs.unsubscribeFromChannels(channels);
    };
  }, [symbolsKey, hasPublicWs, isPublicConnected]);

  const activeTickSize = useStore(
    useMarketStore(symbol ?? ''),
    (state) => state.activeTickSize,
  );

  useEffect(() => {
    if (!hasPublicWs || !isPublicConnected) return;
    const { publicWs } = useWebSocketStore.getState();
    if (!publicWs) return;
    const symbolsList = depthSymbolsKey ? depthSymbolsKey.split(',') : [];
    const channels = new Set<string>();

    symbolsList.forEach((depthSymbol) => {
      const { marketSpec, activeTickSize: currentTick } =
        useMarketStore(depthSymbol).getState();
      const displayTick = currentTick || marketSpec.defaultTickSize;
      const finestTick = marketSpec.tickSizes[0];

      channels.add(
        displayTick
          ? `${depthSymbol}@depth_${displayTick}`
          : `${depthSymbol}@depth`,
      );
      channels.add(
        finestTick
          ? `${depthSymbol}@depth_${finestTick}`
          : `${depthSymbol}@depth`,
      );
    });

    const channelList = Array.from(channels);
    if (channelList.length === 0) return;
    publicWs.subscribeToChannels(channelList);
    return () => {
      publicWs.unsubscribeFromChannels(channelList);
    };
  }, [depthSymbolsKey, hasPublicWs, isPublicConnected, symbol, activeTickSize]);

  useOpenInterest({
    symbols,
    disabled: symbols.length === 0,
  });

  return <></>;
}
